/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useState, useRef, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { setNewThread, setSelectedContact } from "@store/slices/chats.slice"
import ContactCard from "@/pages/chats/components/ContactCard"
import { Badge } from "@/components/ui/badge"
import { useFetchChats } from "@/services/query/chats.query"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { RefreshCw } from "lucide-react"
import {
  addTemporaryThread,
  updateTemporaryThread,
} from "./utils/message_helper"

function ChatLeftPanel() {
  const dispatch = useDispatch()
  const chats = useSelector((state) => state.chats)
  const [searchQuery, setSearchQuery] = useState("")
  const [isRequested, setIsRequested] = useState(false)
  const [isUnread, setIsUnread] = useState(false)
  const [isAll, setIsAll] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const observerTarget = useRef(null)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchChats(100, searchQuery, isRequested, isUnread)

  const queryClient = useQueryClient()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["chats"] }),
      queryClient.invalidateQueries({ queryKey: ["thread-chats"] }),
    ])
    setIsRefreshing(false)
  }

  // Improved observer logic with debouncing
  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0]
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        setTimeout(() => {
          fetchNextPage()
        }, 100) // Small delay to prevent multiple triggers
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const element = observerTarget.current
    const option = { threshold: 0.1 }
    const observer = new IntersectionObserver(handleObserver, option)

    if (element) observer.observe(element)
    return () => {
      if (element) observer.unobserve(element)
    }
  }, [handleObserver])

  console.log("chats", chats)

  /// Last message update
  useEffect(() => {
    const { newThread } = chats
    if (!newThread) {
      return
    }

    // Transform the WebSocket message to match thread structure
    const threadData = {
      thread_id: newThread.thread_id,
      user_name: newThread.user_name,
      phone_number: newThread.phone,
      last_message: {
        content: newThread.message,
        type: newThread.message_type,
        modified_at: newThread.thread_modified_at,
      },
      is_requested: newThread.thread_is_requested,
      unread_count: newThread.thread_unread_count,
      modified_at: newThread.thread_modified_at,
      is_read: !newThread.thread_unread_count,
    }

    const isAvailable = data?.threads?.find(
      (thread) => thread.thread_id === threadData.thread_id
    )

    if (isAvailable) {
      const optimisticThread = {
        message: newThread.message,
        thread_unread_count: newThread.thread_unread_count,
        thread_is_requested: newThread.thread_is_requested,
      }

      updateTemporaryThread(
        queryClient,
        threadData.thread_id,
        "",
        isUnread,
        isRequested,
        optimisticThread
      )
      //queryClient.invalidateQueries({ queryKey: ["chats"] })
    } else {
      addTemporaryThread(queryClient, "", isUnread, isRequested, newThread)
      // queryClient.invalidateQueries({ queryKey: ["chats"] })
      // queryClient.setQueryData(["chats"], (oldData) => {
      //   if (!oldData?.threads) return { threads: [threadData] }
      //   return {
      //     ...oldData,
      //     threads: oldData.threads.map((thread) =>
      //       thread.thread_id === threadData.thread_id ? threadData : thread
      //     ),
      //   }
      // })
    }

    dispatch(setNewThread(null))
  }, [chats.newThread])

  const openChatDetails = (contact) => {
    dispatch(setSelectedContact(contact))
  }

  const handleAll = () => {
    setIsAll(true)
    setIsRequested(false)
    setIsUnread(false)
  }

  const handleRequested = () => {
    setIsAll(false)
    setIsRequested(!isRequested)
  }

  const handleUnread = () => {
    setIsAll(false)
    setIsUnread(!isUnread)
  }

  return (
    <div className="w-full h-full flex flex-col border-r border-gray-200 dark:border-gray-800">
      <div className="p-2 sm:p-3 border-b flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">Chats</h2>
          <button
            onClick={handleRefresh}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`h-4 w-4 text-gray-500 
                ${isRefreshing ? "animate-spin" : "hover:text-gray-700"}`}
            />
          </button>
        </div>

        <div className="flex-1">
          <input
            type="text"
            placeholder="Search contacts..."
            className="w-full p-2.5 rounded-lg border bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex justify-start items-center gap-2 overflow-x-auto py-1">
          <Badge
            variant={isAll ? "default" : "outline"}
            className="py-1 px-3 cursor-pointer"
            onClick={handleAll}
          >
            ALL
          </Badge>
          <Badge
            variant={isUnread ? "default" : "outline"}
            className="py-1 px-3 cursor-pointer"
            onClick={handleUnread}
          >
            Unread
          </Badge>
          <Badge
            variant={isRequested ? "default" : "outline"}
            className="py-1 px-3 cursor-pointer"
            onClick={handleRequested}
          >
            Requested
          </Badge>
        </div>
      </div>
      <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {isLoading && !data?.threads && (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive" className="m-4">
            <AlertDescription className="flex justify-between items-center">
              <span>{error?.message || "Failed to load chats"}</span>
              <button onClick={() => refetch()} className="text-xs underline">
                Retry
              </button>
            </AlertDescription>
          </Alert>
        )}

        {/* Show existing threads even during error */}
        <div className="space-y-1">
          {data?.threads?.map((contact) => (
            <div
              key={contact.thread_id}
              onClick={() => openChatDetails(contact)}
              className={`w-full text-left shadow-none border-b transition-colors ${
                chats?.selectedContact?.thread_id === contact.thread_id
                  ? "bg-gray-300 dark:bg-black"
                  : "hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <ContactCard
                contact={{
                  ...contact,
                  is_read:
                    chats?.selectedContact?.thread_id === contact.thread_id
                      ? true
                      : contact.is_read,
                }}
                isSelected={
                  chats?.selectedContact?.thread_id === contact.thread_id
                }
              />
            </div>
          ))}
        </div>

        {/* Observer and loading indicator */}
        <div
          ref={observerTarget}
          className="h-20 w-full flex justify-center items-center"
        >
          {isFetchingNextPage && (
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          )}
        </div>

        {/* Empty state */}
        {!isLoading && !isError && data?.threads?.length === 0 && (
          <div className="flex items-center justify-center h-32 text-gray-500">
            No chats found
          </div>
        )}
      </div>
    </div>
  )
}

export default ChatLeftPanel
